/**
 * A person profile can send the reader out to a Compass study page ("Explore in
 * Compass") or to a battle trail. Once they land there the person trail held in
 * router state is gone, so nothing points back to the profile they left.
 *
 * The profile stamps the outbound hop here; PersonReturnPill reads it on the
 * destination and offers the way back. sessionStorage (not local) on purpose —
 * the hop belongs to this tab's reading session and should not outlive it.
 */

function getStorage() {
  if (typeof window !== "undefined" && window.sessionStorage) {
    return window.sessionStorage;
  }

  return {
    getItem() {
      return null;
    },
    setItem() {},
    removeItem() {},
  };
}

const storage = getStorage();

const PERSON_RETURN_KEY = "personReturn";

/**
 * The pill is mounted above the routes, so its effect reads storage before a
 * page-level effect gets to re-stamp the hop (battle trails do this as the
 * reader moves between battles). This event lets it pick that up in the same
 * commit instead of waiting for the next navigation.
 */
const CHANGE_EVENT = "ctb:person-return-change";

function emitChange() {
  if (typeof window === "undefined") return;

  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch (error) {
    // fail silently
  }
}

export function subscribeToPersonReturn(handler) {
  if (typeof window === "undefined") return () => {};

  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

export function savePersonReturn({ personId, personName, route, label } = {}) {
  if (!personId || !route) return;

  try {
    storage.setItem(
      PERSON_RETURN_KEY,
      JSON.stringify({
        personId,
        personName: personName || "",
        route,
        label: label || "",
        savedAt: Date.now(),
      })
    );
  } catch (error) {
    // fail silently
  }

  emitChange();
}

export function getPersonReturn() {
  try {
    const value = storage.getItem(PERSON_RETURN_KEY);
    if (!value) return null;

    const parsed = JSON.parse(value);
    return parsed?.personId && parsed?.route ? parsed : null;
  } catch (error) {
    return null;
  }
}

export function clearPersonReturn() {
  // Nothing stored means nothing changed. Skipping the event matters: the pill
  // clears on every person route, and an unconditional emit would call back
  // into it forever.
  if (!getPersonReturn()) return;

  try {
    storage.removeItem(PERSON_RETURN_KEY);
  } catch (error) {
    // fail silently
  }

  emitChange();
}
